import {
  Outlet,
  createFileRoute,
  useLocation,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import Close from "~icons/material-symbols/close";
import Menu from "~icons/material-symbols/menu";
import { css } from "../../styled-system/css";
import logo from "../assets/logo.png";
import {
  SessionProvider,
  User as PortalUser,
  useServiceSettingsHref,
} from "@pocketsign/portal";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { AdminSidebar } from "../components/admin-sidebar";
import { TenantLabel } from "../components/tenant-label";
import {
  type AdminAccessIssue,
  getAdminAccessIssue,
  isPortalLoginRedirect,
} from "../libs/admin/access-errors";
import type { AdminReloginReason } from "../libs/admin/access-errors";
import { getAdminReloginDecision } from "../libs/admin/relogin-decision";
import { debugLog } from "../libs/debug-log";
import { getPortalEnvironment } from "../libs/portal-env";
import { usePortalLoggedIn } from "../libs/portal-auth";

const getConfiguredServiceSlug = () => {
  const configuredServiceSlug = import.meta.env.VITE_SERVICE_SLUG?.trim();
  return configuredServiceSlug || "";
};

const getPortalCommonManagementUrl = () =>
  import.meta.env.VITE_PORTAL_COMMON_MANAGEMENT_URL?.trim() || "";

export const Route = createFileRoute("/admin")({
  component: RouteComponent,
});

function LoadingScreen() {
  return (
    <div
      data-panda-theme="v2"
      className={css({
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bg: "background.background",
        color: "text.secondary",
        fontWeight: "bold",
      })}
    >
      Loading...
    </div>
  );
}

const reloginScreenCopy: Record<
  AdminReloginReason,
  {
    title: string;
    description: string;
  }
> = {
  portal_token_error: {
    title: "ログイン状態を確認できませんでした",
    description:
      "ログイン状態を確認できませんでした。もう一度お試しいただくか、再度ログインしてください。",
  },
  backend_unauthenticated: {
    title: "ログインが必要です",
    description:
      "ログアウトされました。もう一度お試しいただくか、再度ログインしてください。",
  },
};

function AdminReloginScreen({
  onRetry,
  onReturnToPortal,
  reason,
}: {
  onRetry: () => void;
  onReturnToPortal: (fallbackUrl: string) => void;
  reason: AdminReloginReason;
}) {
  const portalCommonManagementUrl = getPortalCommonManagementUrl();
  const copy = reloginScreenCopy[reason];

  return (
    <div
      data-panda-theme="v2"
      className={css({
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bg: "background.background",
        px: "4",
      })}
    >
      <div
        className={css({
          width: "full",
          maxWidth: "480px",
          bg: "white",
          borderRadius: "lg",
          boxShadow: "md",
          p: "8",
          textAlign: "center",
        })}
      >
        <h1
          className={css({
            fontSize: "2xl",
            fontWeight: "bold",
            color: "text.primary",
            mb: "4",
          })}
        >
          {copy.title}
        </h1>
        <p
          className={css({
            color: "text.secondary",
            lineHeight: "1.7",
            mb: "6",
            textAlign: "left",
          })}
        >
          {copy.description}
        </p>
        <div
          className={css({
            display: "flex",
            gap: "3",
            justifyContent: "center",
            flexWrap: "wrap",
          })}
        >
          <button
            type="button"
            onClick={onRetry}
            className={css({
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid",
              borderColor: "border.secondary",
              bg: "white",
              color: "text.primary",
              py: "2",
              px: "4",
              borderRadius: "md",
              fontWeight: "bold",
              cursor: "pointer",
            })}
          >
            再試行
          </button>
          {portalCommonManagementUrl && (
            <button
              type="button"
              onClick={() => {
                debugLog("[admin-auth] User returned to PocketSign management", {
                  href: window.location.href,
                  pathname: window.location.pathname,
                  destination: portalCommonManagementUrl,
                });
                onReturnToPortal(portalCommonManagementUrl);
              }}
              className={css({
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                bg: "surface.accentPrimary",
                color: "white",
                py: "2",
                px: "4",
                borderRadius: "md",
                fontWeight: "bold",
                cursor: "pointer",
                _hover: {
                  bg: "action.accentPrimaryHover",
                },
              })}
            >
              ポケットサイン共通管理に戻る
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AuthGate({
  accessIssue,
  children,
  clearAccessIssue,
}: {
  accessIssue: AdminAccessIssue | null;
  children: React.ReactNode;
  clearAccessIssue: () => void;
}) {
  const { authError, isChecking, isLoggedIn, refresh, returnToPortal } =
    usePortalLoggedIn();
  const lastReloginLogKeyRef = useRef<string | null>(null);

  const reloginDecision = getAdminReloginDecision({ accessIssue, authError });
  const reloginReason = reloginDecision?.reason ?? null;

  useEffect(() => {
    if (!reloginDecision) {
      lastReloginLogKeyRef.current = null;
      return;
    }

    const logPayload = {
      reason: reloginDecision.reason,
      trigger: reloginDecision.trigger,
      authErrorMessage: authError?.message,
      accessIssue,
      isChecking,
      isLoggedIn,
      href: window.location.href,
      pathname: window.location.pathname,
    };
    const logKey = JSON.stringify(logPayload);
    if (lastReloginLogKeyRef.current === logKey) {
      return;
    }
    lastReloginLogKeyRef.current = logKey;

    debugLog("[admin-auth] Relogin screen triggered", logPayload);
  }, [
    accessIssue,
    authError,
    isChecking,
    isLoggedIn,
    reloginDecision?.reason,
    reloginDecision?.trigger,
  ]);

  if (reloginReason) {
    return (
      <AdminReloginScreen
        reason={reloginReason}
        onRetry={() => {
          clearAccessIssue();
          refresh();
        }}
        onReturnToPortal={(fallbackUrl) => {
          clearAccessIssue();
          void returnToPortal(fallbackUrl);
        }}
      />
    );
  }

  if (!isLoggedIn) {
    return <LoadingScreen />;
  }

  return <Layout>{children}</Layout>;
}

function RouteComponent() {
  const location = useLocation();
  const portalEnvironment = getPortalEnvironment();
  const callbackUrl = window.location.href;
  const configuredServiceSlug = getConfiguredServiceSlug();
  const sessionServiceSlug = configuredServiceSlug || undefined;
  const [accessIssue, setAccessIssue] = useState<AdminAccessIssue | null>(null);
  const reportAccessIssue = (error: unknown) => {
    const issue = getAdminAccessIssue(error);
    if (!issue) {
      return;
    }
    setAccessIssue((currentIssue) => currentIssue ?? issue);
  };
  const [myQueryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            reportAccessIssue(error);
          },
        }),
        mutationCache: new MutationCache({
          onError: (error) => {
            reportAccessIssue(error);
          },
        }),
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              if (isPortalLoginRedirect(error) || getAdminAccessIssue(error)) {
                return false;
              }
              return failureCount < 3;
            },
          },
        },
      }),
  );

  useEffect(() => {
    debugLog("[admin-route] Location changed", {
      href: window.location.href,
      pathname: location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    });
  }, [location.pathname, JSON.stringify(location.search)]);

  useEffect(() => {
    debugLog("[admin-route] SessionProvider config", {
      callbackUrl,
      portalEnvironment,
      serviceSlug: sessionServiceSlug ?? "",
    });
  }, [callbackUrl, portalEnvironment, sessionServiceSlug]);

  return (
    <SessionProvider
      environment={portalEnvironment}
      serviceSlug={sessionServiceSlug}
      callbackUrl={callbackUrl}
    >
      <QueryClientProvider client={myQueryClient}>
        <AuthGate
          accessIssue={accessIssue}
          clearAccessIssue={() => setAccessIssue(null)}
        >
          <Outlet />
        </AuthGate>
      </QueryClientProvider>
    </SessionProvider>
  )
}

const UserButtonSection = () => {
  return (
    <div className={css({
      px: "4",
      display: "flex",
      alignItems: "center",
    })}>
      <PortalUser />
    </div>
  );
}

const Header = (
  { isMenuOpen, setIsMenuOpen }: { isMenuOpen: boolean, setIsMenuOpen: (isOpen: boolean) => void }
) => {
  return (
    <header
      className={css({
        h: "52px",
        bg: "white",
        w: "full",
        px: "4",
        py: "2",
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid",
        borderColor: "border.secondary",
        flexShrink: 0,
      })}
    >
      <div
        className={css({
          display: { base: "none", md: "flex" },
          alignItems: "center",
          width: "full",
        })}
      >
        <img
          src={logo}
          alt="logo"
          className={css({
            h: "15px",
          })}
        />
        <div
          className={css({
            color: "text.secondary",
            pl: "2",
            pr: "4",
          })}
        >
          地域ポイント
        </div>
        <div
          className={css({
            color: "text.secondary",
            borderLeft: "1px solid",
            borderColor: "border.secondary",
            px: "4",
          })}
        >
          管理者コンソール
        </div>
        <div
          className={css({
            color: "text.secondary",
            borderLeft: "1px solid",
            borderColor: "border.secondary",
            px: "4",
          })}
        >
          <TenantLabel />
        </div>
        <div className={css({
          ml: "auto",
        })} />
        <UserButtonSection />
      </div>
      <button
        type="button"
        className={css({
          display: { base: "flex", md: "none" },
          ml: "auto",
          alignItems: "center",
          justifyContent: "center",
          p: "2",
          cursor: "pointer",
        })}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        {isMenuOpen ? <Close /> : <Menu />}
      </button>
    </header>
  )
}

const SidebarOuterTint = ({ isMenuOpen, setIsMenuOpen }: { isMenuOpen: boolean, setIsMenuOpen: (isOpen: boolean) => void }) => {
  if (!isMenuOpen) {
    return null;
  }
  return (
    <button
      type="button"
      className={css({
        position: "fixed",
        top: "52px",
        left: 0,
        right: 0,
        bottom: 0,
        bg: "rgba(0, 0, 0, 0.5)",
        zIndex: "5",
        display: { base: "block", md: "none" },
      })}
      onClick={() => setIsMenuOpen(false)}
    />
  )
}

const Layout = ({ children }: { children: React.ReactNode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const serviceSettingsHref = useServiceSettingsHref();
  const handleOpenAccountSettings = () => {
    debugLog("[admin-auth] Opening account settings from sidebar", {
      href: window.location.href,
      pathname: window.location.pathname,
      destination: serviceSettingsHref,
    });
    window.location.assign(serviceSettingsHref);
  };

  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        position: "relative",
      })}
      data-panda-theme="v1"
    >
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <div
        className={css({
          display: "flex",
          flex: 1,
          overflow: "hidden",
        })}
      >
        <AdminSidebar
          isMenuOpen={isMenuOpen}
          onOpenAccountSettings={handleOpenAccountSettings}
        />
        <SidebarOuterTint isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
        <main
          className={css({
            bg: "background.background",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          })}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
